import CakeIcon from '@mui/icons-material/Cake';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Paper,
} from '@mui/material';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import strings from '../../../common/Translation/Translate';

interface BirthdaysAnniversariesProps {
  title: string;
  data: Array<{
    userId: number;
    fullName: string;
    email: string;
    phone: string;
    type: 'BIRTHDAY' | 'ANNIVERSARY';
    eventDate: string;
  }>;
}

// Helper function to compute relative date timing
const getRelativeDate = (eventDate: string): { label: string; color: string } => {
  const today = moment().startOf('day');
  const event = moment(eventDate).startOf('day');
  const diffDays = event.diff(today, 'days');

  if (diffDays === 0) {
    return { label: 'Today', color: '#1976D2' };
  } else if (diffDays === 1) {
    return { label: 'Tomorrow', color: '#2E7D32' };
  } else if (diffDays > 1 && diffDays <= 7) {
    return { label: `In ${diffDays} days`, color: '#ED6C02' };
  } else if (diffDays < 0) {
    return { label: `${Math.abs(diffDays)} days ago`, color: '#9E9E9E' };
  } else {
    return { label: `In ${diffDays} days`, color: '#757575' };
  }
};

// Format date as "12 Aug"
const formatShortDate = (date: string): string => {
  return moment(date).format('DD MMM');
};

const BirthdaysAnniversaries: FC<BirthdaysAnniversariesProps> = ({ title, data }) => {
  const theme = useTheme();

  // Sort data by date (upcoming first, then past)
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const dateA = moment(a.eventDate);
      const dateB = moment(b.eventDate);
      const today = moment().startOf('day');
      
      // Upcoming events first
      const aIsUpcoming = dateA.isSameOrAfter(today);
      const bIsUpcoming = dateB.isSameOrAfter(today);
      
      if (aIsUpcoming && !bIsUpcoming) return -1;
      if (!aIsUpcoming && bIsUpcoming) return 1;
      
      // Then sort by date
      return dateA.diff(dateB);
    });
  }, [data]);

  // Action handlers
  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleWhatsApp = (phone: string) => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const handleSendWish = (item: typeof data[0]) => {
    const subject = encodeURIComponent(
      item.type === 'BIRTHDAY' 
        ? `Happy Birthday ${item.fullName}!` 
        : `Happy Anniversary ${item.fullName}!`
    );
    const body = encodeURIComponent(
      item.type === 'BIRTHDAY'
        ? `Dear ${item.fullName},\n\nWishing you a wonderful birthday filled with joy and happiness!\n\nBest regards`
        : `Dear ${item.fullName},\n\nCongratulations on your anniversary! Wishing you many more years of happiness together.\n\nBest regards`
    );
    window.location.href = `mailto:${item.email}?subject=${subject}&body=${body}`;
  };

  const tableHeaders = ['Date', 'Type', 'Name', 'Phone', 'Email', 'Actions'];

  return (
    <Card 
      sx={{ 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}
    >
      <Box 
        sx={{ 
          p: 2.5, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography fontWeight={700} sx={{ fontSize: '1.125rem', color: theme.palette.text.primary }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 0 }}>
        {sortedData.length > 0 ? (
          <TableContainer 
            component={Paper}
            sx={{
              maxHeight: '60vh',
              '& .MuiTable-root': {
                minWidth: 650,
              },
            }}
          >
            <Table stickyHeader aria-label="birthdays and anniversaries table" size="medium">
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell
                      key={header}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        borderRight: `1px solid ${theme.palette.primary.dark}`,
                        '&:last-child': {
                          borderRight: 'none',
                        },
                        textAlign: header === 'Actions' ? 'right' : 'left',
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((item, index) => {
                  const relativeDate = getRelativeDate(item.eventDate);
                  const shortDate = formatShortDate(item.eventDate);
                  const isBirthday = item.type === 'BIRTHDAY';
                  
                  // Color scheme: warm/yellow for birthday, purple/rose for anniversary
                  const accentColor = isBirthday ? '#FF9800' : '#E91E63';
                  const backgroundColor = isBirthday ? '#FFF3E0' : '#FCE4EC';
                  const iconColor = isBirthday ? '#FF6F00' : '#C2185B';

                  return (
                    <TableRow
                      key={`${item.userId}-${item.eventDate}-${index}`}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      {/* Date Column */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color: accentColor,
                              fontSize: '1rem',
                            }}
                          >
                            {shortDate}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Type Column */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {isBirthday ? (
                            <CakeIcon sx={{ fontSize: '20px', color: iconColor }} />
                          ) : (
                            <FavoriteIcon sx={{ fontSize: '20px', color: iconColor }} />
                          )}
                          <Chip
                            label={isBirthday ? 'Birthday' : 'Anniversary'}
                            size="small"
                            sx={{
                              backgroundColor: accentColor,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: '24px',
                            }}
                          />
                        </Box>
                      </TableCell>

                      {/* Name Column */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.fullName}
                        </Typography>
                      </TableCell>

                      {/* Phone Column */}
                      <TableCell>
                        {item.phone ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PhoneIcon
                              sx={{
                                fontSize: '14px',
                                color: theme.palette.text.disabled,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.8rem',
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {item.phone}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.8rem',
                              color: theme.palette.text.disabled,
                              fontStyle: 'italic',
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Email Column */}
                      <TableCell>
                        {item.email ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <EmailIcon 
                              sx={{ 
                                fontSize: '14px', 
                                color: theme.palette.text.disabled,
                              }} 
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.8rem',
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {item.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.8rem',
                              color: theme.palette.text.disabled,
                              fontStyle: 'italic',
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions Column */}
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
                          {item.phone && (
                            <>
                              <Tooltip title="Call" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCall(item.phone)}
                                  sx={{
                                    backgroundColor: theme.palette.primary.light,
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                      backgroundColor: theme.palette.primary.main,
                                      color: 'white',
                                    },
                                    width: '32px',
                                    height: '32px',
                                  }}
                                  aria-label="Call"
                                >
                                  <PhoneIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="WhatsApp" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleWhatsApp(item.phone)}
                                  sx={{
                                    backgroundColor: '#25D366',
                                    color: 'white',
                                    '&:hover': {
                                      backgroundColor: '#20BA5A',
                                    },
                                    width: '32px',
                                    height: '32px',
                                  }}
                                  aria-label="WhatsApp"
                                >
                                  <Box
                                    component="svg"
                                    sx={{
                                      width: '16px',
                                      height: '16px',
                                      fill: 'currentColor',
                                    }}
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                  </Box>
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {item.email && (
                            <Tooltip title="Send Wish" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleSendWish(item)}
                                sx={{
                                  backgroundColor: accentColor,
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: iconColor,
                                  },
                                  width: '32px',
                                  height: '32px',
                                }}
                                aria-label="Send Wish"
                              >
                                <EmailIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {strings.no_data_found}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default BirthdaysAnniversaries;
